import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TChannel } from '@feathr/blackbox';

import type { TCreativeType, TDisplayCreativeType, TMetaCreativeType } from '../types';

/** Step in the add creative flow */
export type TAddCreativeStep = 'channel' | 'creative-type' | 'campaign';

/** Option configuration for creative type selection (compatible with Radios IRadioOption) */
export interface ICreativeTypeOption {
  /** Unique identifier for the option */
  id: TDisplayCreativeType | TMetaCreativeType;
  /** Display name */
  name: string;
  /** Description text */
  description: string;
}

/**
 * Return type for the useAddCreativeFlow hook.
 */
export interface IUseAddCreativeFlowResult {
  /** Current step in the flow */
  step: TAddCreativeStep;
  /** Currently selected channel */
  selectedChannel: TChannel | undefined;
  /** Currently selected creative type */
  selectedCreativeType: TCreativeType | undefined;
  /** Currently selected campaign ID (for project-level flow) */
  selectedCampaignId: string | undefined;
  /** Handler for channel selection */
  handleChannelSelect: (channel: TChannel) => void;
  /** Handler for creative type selection */
  handleCreativeTypeSelect: (creativeType: TCreativeType) => void;
  /** Handler for campaign selection */
  handleCampaignSelect: (campaignId: string | undefined) => void;
  /** Handler for Next button on channel step */
  handleNextFromChannel: () => void;
  /** Handler for Next button on creative type step */
  handleNextFromCreativeType: () => void;
  /** Handler for Next button on campaign step */
  handleNextFromCampaign: () => void;
  /** Handler for Back button */
  handleBack: () => void;
  /** Handler for closing the modal */
  handleClose: () => void;
  /** Whether the Next button should be disabled on channel step */
  isNextDisabledOnChannel: boolean;
  /** Whether the Next button should be disabled on creative type step */
  isNextDisabledOnCreativeType: boolean;
  /** Modal title for current step */
  modalTitle: string;
  /** Modal description for current step */
  modalDescription: string;
  /** Creative type options based on selected channel */
  creativeTypeOptions: ICreativeTypeOption[];
  /** Whether the Back button should be hidden */
  shouldHideBackButton: boolean;
  /** Whether the campaign step should be shown */
  shouldShowCampaignStep: boolean;
  /** Event ID for filtering campaigns */
  eventId: string | undefined;
}

export function useAddCreativeFlow({
  onClose,
  onComplete,
  initialStep = 'channel',
  initialChannel,
  availableChannels,
  eventId,
  campaignId,
}: {
  onClose: () => void;
  onComplete: (channel: TChannel, creativeType: TCreativeType, campaignId?: string) => void;
  initialStep?: TAddCreativeStep;
  initialChannel?: TChannel;
  availableChannels?: TChannel[];
  eventId?: string;
  campaignId?: string;
}): IUseAddCreativeFlowResult {
  const { t } = useTranslation();

  const shouldShowCampaignStep = !campaignId;

  const shouldAutoSelect = availableChannels?.length === 1 && initialStep !== 'creative-type';
  const autoSelectedChannel = shouldAutoSelect ? availableChannels[0] : undefined;

  const [step, setStep] = useState<TAddCreativeStep>(
    shouldAutoSelect ? 'creative-type' : initialStep,
  );
  const [selectedChannel, setSelectedChannel] = useState<TChannel | undefined>(
    initialChannel ?? autoSelectedChannel,
  );
  const [selectedCreativeType, setSelectedCreativeType] = useState<TCreativeType | undefined>();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>();

  const shouldHideBackButton = shouldAutoSelect;

  const resetState = useCallback((): void => {
    setStep('channel');
    setSelectedChannel(undefined);
    setSelectedCreativeType(undefined);
    setSelectedCampaignId(undefined);
  }, []);

  const handleClose = useCallback((): void => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleBack = useCallback((): void => {
    if (step === 'campaign') {
      setSelectedCampaignId(undefined);
      setStep('creative-type');
    } else {
      setSelectedCreativeType(undefined);
      setStep('channel');
    }
  }, [step]);

  const handleChannelSelect = useCallback((channel: TChannel): void => {
    setSelectedChannel(channel);
  }, []);

  const handleCreativeTypeSelect = useCallback((creativeType: TCreativeType): void => {
    setSelectedCreativeType(creativeType);
  }, []);

  const handleCampaignSelect = useCallback((selectedId: string | undefined): void => {
    setSelectedCampaignId(selectedId);
  }, []);

  const handleNextFromChannel = useCallback((): void => {
    if (selectedChannel) {
      setStep('creative-type');
    }
  }, [selectedChannel]);

  const handleNextFromCreativeType = useCallback((): void => {
    if (selectedChannel && selectedCreativeType) {
      // Skip campaign step for 'duplicate' - DuplicateCreativeModal handles its own campaign selection
      if (shouldShowCampaignStep && selectedCreativeType !== 'duplicate') {
        setStep('campaign');
      } else {
        // Campaign already provided or duplicate flow (has its own campaign selector)
        onComplete(selectedChannel, selectedCreativeType, campaignId);
        resetState();
      }
    }
  }, [
    selectedChannel,
    selectedCreativeType,
    shouldShowCampaignStep,
    onComplete,
    campaignId,
    resetState,
  ]);

  const handleNextFromCampaign = useCallback((): void => {
    if (selectedChannel && selectedCreativeType) {
      onComplete(selectedChannel, selectedCreativeType, selectedCampaignId);
      resetState();
    }
  }, [selectedChannel, selectedCreativeType, selectedCampaignId, onComplete, resetState]);

  const getChannelTitle = useCallback(
    (channel: TChannel): string => {
      return channel === 'display' ? t('Display Ad') : t('Meta');
    },
    [t],
  );

  const displayCreativeTypeOptions: ICreativeTypeOption[] = useMemo(
    () => [
      {
        id: 'image_video',
        name: t('Add Image/Video Creative'),
        description: t('Upload image or video files for your creative'),
      },
      {
        id: 'build',
        name: t('Build Creative'),
        description: t('Design a creative using the builder tool'),
      },
      {
        id: 'duplicate',
        name: t('Duplicate Existing Creative'),
        description: t('Copy an existing creative from your library'),
      },
      {
        id: 'ad_tag',
        name: t('Add Ad Tag Creative'),
        description: t('Use third-party ad tag HTML markup'),
      },
    ],
    [t],
  );

  const metaCreativeTypeOptions: ICreativeTypeOption[] = useMemo(
    () => [
      {
        id: 'image_video',
        name: t('Add Image/Video Creative'),
        description: t('Upload image or video files for your creative'),
      },
      {
        id: 'duplicate',
        name: t('Duplicate Existing Creative'),
        description: t('Copy an existing creative from your library'),
      },
    ],
    [t],
  );

  const creativeTypeOptions =
    selectedChannel === 'display' ? displayCreativeTypeOptions : metaCreativeTypeOptions;

  const getModalTitle = (): string => {
    if (step === 'channel') {
      return t('Choose Channel');
    }
    if (step === 'campaign') {
      return t('Select Campaign');
    }
    if (selectedChannel) {
      return t('Choose {{channel}} Creative Type', { channel: getChannelTitle(selectedChannel) });
    }
    return t('Choose Creative Type');
  };

  const modalTitle = getModalTitle();

  const getModalDescription = (): string => {
    if (step === 'channel') {
      return t('Select the channel for your creative.');
    }
    if (step === 'campaign') {
      return t('Optionally select a campaign to add the creative to.');
    }
    return t('Select the type of creative you want to create.');
  };

  const modalDescription = getModalDescription();

  return {
    step,
    selectedChannel,
    selectedCreativeType,
    selectedCampaignId,
    handleChannelSelect,
    handleCreativeTypeSelect,
    handleCampaignSelect,
    handleNextFromChannel,
    handleNextFromCreativeType,
    handleNextFromCampaign,
    handleBack,
    handleClose,
    isNextDisabledOnChannel: !selectedChannel,
    isNextDisabledOnCreativeType: !selectedCreativeType,
    modalTitle,
    modalDescription,
    creativeTypeOptions,
    shouldHideBackButton,
    shouldShowCampaignStep,
    eventId,
  };
}
